/**
 * Room Creation Utilities
 *
 * Pure utility functions for multi-click polygon room creation.
 * No React dependencies — all functions are stateless and testable.
 */

import {
  isPolygonClockwise,
  calculatePolygonArea,
  calculatePolygonCentroid,
  doLineSegmentsIntersect,
} from './roomPolygonUtils';
import polygonClipping from 'polygon-clipping';
import { findFloorY } from './floorPlanUtils';
import { WALL_THICKNESS, createStructureWall } from './wallCreationUtils';
import { getWallEndpoints } from './snapToWall';
import { DEFAULT_WALL_TRIM } from '@shared-lib/millwork';
import {
  snapVertexToAnchorAngle,
  snapVertexToNeighborAngles,
} from './vertexSnap';
import {
  gatherPointConstraints,
  gatherLineConstraints,
  resolveSnap,
} from './roomSnapInference';

// --- Constants ---

/** Snap distance for vertices (1 foot in meters) */
export const VERTEX_SNAP_THRESHOLD = 0.3048;

/** Snap distance for closing the polygon near the first vertex */
export const CLOSE_SNAP_THRESHOLD = 0.3048;

/** Minimum distance between consecutive vertices (meters) */
const MIN_VERTEX_DISTANCE = 0.01;

/**
 * Collinearity tolerance for wall matching (meters).
 * Structure walls are offset outward by halfThickness from their source
 * polygon edge, so a polygon edge legitimately matching an existing wall
 * sits at perpDist ≈ halfThickness from the wall's centerline. Allowing
 * up to one full wall-thickness keeps slack for floating-point and
 * scan-import drift while rejecting walls that are clearly off-edge.
 */
const COLLINEARITY_TOLERANCE = WALL_THICKNESS;

/** Endpoint slack for full-overlap wall matching (meters) */
const OVERLAP_TOLERANCE = 0.05;

// --- Geometry Helpers ---

/**
 * Find the nearest point on line segment AB to point P.
 * Uses vector projection clamped to [0,1].
 * @param {{x: number, z: number}} p - Query point
 * @param {{x: number, z: number}} a - Segment start
 * @param {{x: number, z: number}} b - Segment end
 * @returns {{x: number, z: number}} Nearest point on segment
 */
export function nearestPointOnSegment(p, a, b) {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const lenSq = dx * dx + dz * dz;
  if (lenSq < 1e-12) return { x: a.x, z: a.z };

  const t = Math.max(
    0,
    Math.min(1, ((p.x - a.x) * dx + (p.z - a.z) * dz) / lenSq)
  );
  return { x: a.x + t * dx, z: a.z + t * dz };
}

// --- Validation ---

/** Overlap below this is two outlines touching along an edge, not a room
 *  counted twice. */
const ROOM_OVERLAP_MIN_M2 = 0.01;

/** Area of a polygon-clipping MultiPolygon: each outer ring less its holes. */
function multiPolygonArea(multi) {
  let total = 0;
  for (const polygon of multi || []) {
    polygon.forEach((ring, i) => {
      const a = Math.abs(calculatePolygonArea(ring));
      total += i === 0 ? a : -a;
    });
  }
  return total;
}

/**
 * The first room on `story` whose floor a drawn outline overlaps — a room
 * drawn inside another counts its floor twice, which is what the walls tool
 * (draw walls across the room, then split it) exists to avoid.
 *
 * A real intersection (`polygon-clipping`), because rooms are concave: a
 * bounding-box or SAT test would refuse a room drawn in an L-room's empty
 * corner. A room with no storey stamped is on every storey, as the split's
 * storey filter reads it.
 *
 * @param {Array<{x: number, z: number}>} vertices the drawn outline
 * @param {Map|Array} roomsData the room store (anything with `.values()`)
 * @param {number|null} story the storey the outline is drawn on
 * @returns {object|null} the room, or null
 */
export function findOverlappingRoom(vertices, roomsData, story) {
  if (!vertices || vertices.length < 3 || !roomsData?.values) return null;
  const drawn = [vertices.map((v) => [v.x, v.z])];
  for (const room of roomsData.values()) {
    if (
      Number.isFinite(story) &&
      Number.isFinite(room?.story) &&
      room.story !== story
    ) {
      continue;
    }
    const corners = room?.floors?.[0]?.polygonCorners;
    if (!corners || corners.length < 3) continue;
    const ring = [corners.map((c) => [c.x, c.z])];
    const overlap = multiPolygonArea(polygonClipping.intersection(drawn, ring));
    if (overlap > ROOM_OVERLAP_MIN_M2) return room;
  }
  return null;
}

/**
 * Validate that a set of vertices form a valid polygon.
 * @param {Array<{x: number, z: number}>} vertices
 * @returns {{valid: boolean, reason: string|null}}
 */
export function validatePolygon(vertices) {
  if (!vertices || vertices.length < 3) {
    return { valid: false, reason: 'Need at least 3 vertices to form a room' };
  }

  // Check for self-intersection
  for (let i = 0; i < vertices.length; i++) {
    const a1 = vertices[i];
    const a2 = vertices[(i + 1) % vertices.length];
    for (let j = i + 2; j < vertices.length; j++) {
      // Skip adjacent edges (they share a vertex)
      if (i === 0 && j === vertices.length - 1) continue;
      const b1 = vertices[j];
      const b2 = vertices[(j + 1) % vertices.length];
      if (doLineSegmentsIntersect(a1, a2, b1, b2)) {
        return {
          valid: false,
          reason: 'Polygon edges must not cross each other',
        };
      }
    }
  }

  // Check for degenerate polygon (near-zero area)
  const points = vertices.map((v) => [v.x, v.z]);
  const area = Math.abs(calculatePolygonArea(points));
  if (area < 0.01) {
    return { valid: false, reason: 'Polygon area is too small' };
  }

  return { valid: true, reason: null };
}

/**
 * Check if adding a new vertex would create a self-intersecting polygon.
 * Tests the new edge (last vertex → newVertex) against all existing edges.
 * @param {Array<{x: number, z: number}>} existingVertices
 * @param {{x: number, z: number}} newVertex
 * @returns {boolean}
 */
export function wouldSelfIntersect(existingVertices, newVertex) {
  if (existingVertices.length < 2) return false;

  const last = existingVertices[existingVertices.length - 1];

  // Check the new edge against all non-adjacent existing edges
  for (let i = 0; i < existingVertices.length - 2; i++) {
    const a1 = existingVertices[i];
    const a2 = existingVertices[i + 1];
    if (doLineSegmentsIntersect(a1, a2, last, newVertex)) {
      return true;
    }
  }

  return false;
}

/**
 * Ensure polygon vertices are in counter-clockwise order (XZ plane, viewed from above).
 * @param {Array<{x: number, z: number}>} vertices
 * @returns {Array<{x: number, z: number}>}
 */
export function ensureCCW(vertices) {
  if (isPolygonClockwise(vertices)) {
    return [...vertices].reverse();
  }
  return vertices;
}

/**
 * Remove consecutive duplicate vertices (within MIN_VERTEX_DISTANCE).
 * Also removes the last vertex if it duplicates the first (closed polygon artifact).
 * @param {Array<{x: number, z: number}>} vertices
 * @returns {Array<{x: number, z: number}>}
 */
export function deduplicateVertices(vertices) {
  if (vertices.length <= 1) return vertices;

  const result = [vertices[0]];
  for (let i = 1; i < vertices.length; i++) {
    const prev = result[result.length - 1];
    const curr = vertices[i];
    const dist = Math.hypot(curr.x - prev.x, curr.z - prev.z);
    if (dist > MIN_VERTEX_DISTANCE) {
      result.push(curr);
    }
  }

  // Remove last if it duplicates first
  if (result.length > 1) {
    const first = result[0];
    const last = result[result.length - 1];
    if (Math.hypot(last.x - first.x, last.z - first.z) <= MIN_VERTEX_DISTANCE) {
      result.pop();
    }
  }

  return result;
}

// --- Metadata derivation ---

/**
 * Derive room metadata (floorY, story) from existing structure data. The new
 * room's wall HEIGHT is derived separately from the wall it's drawn against
 * (see `sharedWallHeight`), not here.
 *
 * A room drawn while a floor is being viewed (`opts.story`, the floor
 * picker's) is a room ON that floor, at that floor's height — a user ruling,
 * 2026-10-22. Without one, the storey of the first room is kept, as before.
 * @param {Object} structureData - The structure data object (used for floorY)
 * @param {Map} roomsData - Map of room ID → room data
 * @param {{ story?: number|null }} [opts] the floor being viewed
 * @returns {{floorY: number, story: number}}
 */
export function deriveRoomMetadata(
  structureData,
  roomsData,
  { story: viewedStory = null } = {}
) {
  if (Number.isFinite(viewedStory)) {
    const onStorey = [...(roomsData?.values?.() ?? [])].filter(
      (r) => r?.story === viewedStory
    );
    return {
      floorY: onStorey.length
        ? findFloorY({ rooms: onStorey })
        : findFloorY(structureData),
      story: viewedStory,
    };
  }

  const floorY = findFloorY(structureData);

  // Derive story from first room
  let story = 0;
  if (roomsData && roomsData.size > 0) {
    const firstRoom = roomsData.values().next().value;
    if (firstRoom?.story !== undefined) {
      story = firstRoom.story;
    }
  }

  return { floorY, story };
}

// --- Wall matching ---

/**
 * Find an existing structure wall that is collinear with and overlaps a polygon edge.
 * @param {{x: number, z: number}} edgeStart
 * @param {{x: number, z: number}} edgeEnd
 * @param {Array} structureWalls - Array of structure wall objects
 * @returns {Object|null} The matching wall, or null
 */
export function findMatchingStructureWall(edgeStart, edgeEnd, structureWalls) {
  if (!structureWalls || structureWalls.length === 0) return null;

  const edgeDx = edgeEnd.x - edgeStart.x;
  const edgeDz = edgeEnd.z - edgeStart.z;
  const edgeLen = Math.hypot(edgeDx, edgeDz);
  if (edgeLen < 0.001) return null;

  // Edge unit vector
  const ux = edgeDx / edgeLen;
  const uz = edgeDz / edgeLen;

  let best = null;
  let bestPerp = Infinity;
  let bestOverlap = -Infinity;

  for (const wall of structureWalls) {
    const endpoints = getWallEndpoints(wall);
    if (!endpoints) continue;

    const { start: ws, end: we } = endpoints;
    const wallDx = we.x - ws.x;
    const wallDz = we.z - ws.z;
    const wallLen = Math.hypot(wallDx, wallDz);
    if (wallLen < 0.001) continue;

    // Check collinearity: cross product of edge direction and wall direction should be ~0
    const cross = ux * (wallDz / wallLen) - uz * (wallDx / wallLen);
    if (Math.abs(cross) > 0.1) continue; // Not parallel

    // Check that the wall line passes close to the edge start
    // Distance from edgeStart to the infinite line through wall
    const toStartX = edgeStart.x - ws.x;
    const toStartZ = edgeStart.z - ws.z;
    const perpDist = Math.abs(
      toStartX * (-wallDz / wallLen) + toStartZ * (wallDx / wallLen)
    );
    if (perpDist > COLLINEARITY_TOLERANCE) continue;

    // Project polygon edge endpoints onto the wall's line.
    const projEdgeStart =
      (edgeStart.x - ws.x) * (wallDx / wallLen) +
      (edgeStart.z - ws.z) * (wallDz / wallLen);
    const projEdgeEnd =
      (edgeEnd.x - ws.x) * (wallDx / wallLen) +
      (edgeEnd.z - ws.z) * (wallDz / wallLen);
    const projMin = Math.min(projEdgeStart, projEdgeEnd);
    const projMax = Math.max(projEdgeStart, projEdgeEnd);

    // The wall occupies [0, wallLen] on its own line. Compute the overlap
    // between the wall segment and the polygon edge segment.
    const overlapStart = Math.max(projMin, 0);
    const overlapEnd = Math.min(projMax, wallLen);
    const overlapLength = Math.max(0, overlapEnd - overlapStart);

    // Strict-match rule: the polygon edge must FULLY overlap the wall
    // (and vice versa) — both segments must coincide on the shared line
    // within OVERLAP_TOLERANCE of each end. Partial overlaps create a
    // fresh structure wall instead of reusing this one.
    const requiredOverlap = Math.max(edgeLen, wallLen) - OVERLAP_TOLERANCE;
    if (overlapLength < requiredOverlap) continue;

    // Among qualifying walls, keep the nearest (smallest perpDist), then the
    // largest overlap. Strictly-better comparison preserves first-wins on ties.
    if (
      perpDist < bestPerp - 1e-9 ||
      (Math.abs(perpDist - bestPerp) <= 1e-9 && overlapLength > bestOverlap)
    ) {
      best = wall;
      bestPerp = perpDist;
      bestOverlap = overlapLength;
    }
  }

  return best;
}

/**
 * Convert interior edge endpoints to structure wall parameters.
 * The structure wall is offset outward from the interior edge by WALL_THICKNESS / 2.
 * Uses the provided outward normal to determine offset direction.
 * @param {{x: number, z: number}} start
 * @param {{x: number, z: number}} end
 * @param {number} floorY
 * @param {number} wallHeight
 * @param {{x: number, z: number}} outwardNormal - Unit outward normal for this edge
 * @returns {{center: {x,y,z}, length: number, rotationY: number}}
 */
export function edgeToWallParams(
  start,
  end,
  floorY,
  wallHeight,
  outwardNormal
) {
  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const length = Math.hypot(dx, dz);
  const rotationY = Math.atan2(-dz, dx);

  const edgeMidX = (start.x + end.x) / 2;
  const edgeMidZ = (start.z + end.z) / 2;

  const halfThickness = WALL_THICKNESS / 2;
  const center = {
    x: edgeMidX + outwardNormal.x * halfThickness,
    y: floorY + wallHeight / 2,
    z: edgeMidZ + outwardNormal.z * halfThickness,
  };
  return { center, length, rotationY };
}

/**
 * Create a room wall object for one polygon edge.
 * The polygonCorners are ordered so that bottomLeft → bottomRight produces
 * a perpDir that points inward (toward the room interior).
 * @param {{x: number, z: number}} start
 * @param {{x: number, z: number}} end
 * @param {string} structureWallId
 * @param {number} floorY
 * @param {number} wallHeight
 * @param {number} story
 * @param {{x: number, z: number}} inwardNormal - Unit inward normal for this edge
 * @returns {Object} Room wall object
 */
export function createRoomWallForEdge(
  start,
  end,
  structureWallId,
  floorY,
  wallHeight,
  story,
  inwardNormal
) {
  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const length = Math.hypot(dx, dz);

  // Room walls sit directly on the clicked interior vertices — no offset.
  const centerX = (start.x + end.x) / 2;
  const centerZ = (start.z + end.z) / 2;

  // Determine bottomLeft/bottomRight ordering so perpDir points INWARD.
  // perpDir = [-wallDir.z, wallDir.x] where wallDir = (bR - bL) / length.
  // We pick the ordering where perpDir aligns with the inwardNormal.

  // If start→end is wallDir, perpDir = [-dz, dx] / length
  const perpDotInward =
    (-dz / length) * inwardNormal.x + (dx / length) * inwardNormal.z;

  // perpDotInward > 0 means perpDir already points inward → keep start→end order
  // perpDotInward < 0 means perpDir points outward → swap to end→start
  const bL = perpDotInward >= 0 ? start : end;
  const bR = perpDotInward >= 0 ? end : start;

  const wallDx = bR.x - bL.x;
  const wallDz = bR.z - bL.z;
  const rotationY = Math.atan2(-wallDz, wallDx);

  return {
    id: `rw_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    center: {
      x: centerX,
      y: floorY + wallHeight / 2,
      z: centerZ,
    },
    dimensions: {
      x: length,
      y: wallHeight,
      z: 0, // Room walls are zero-thickness
    },
    rotation: { x: 0, y: rotationY, z: 0 },
    polygonCorners: [
      { x: bL.x, y: floorY, z: bL.z },
      { x: bR.x, y: floorY, z: bR.z },
      { x: bR.x, y: floorY + wallHeight, z: bR.z },
      { x: bL.x, y: floorY + wallHeight, z: bL.z },
    ],
    // The structure wall reference lives at
    // `alignment.structureWallId`. `parentWallIds` is intentionally not
    // set on walls — it's meaningful on openings (where it identifies
    // the parent wall) but redundant on walls, where it was just
    // duplicating the structure wall id. Scan-derived walls don't set
    // it; user-created walls now match.
    alignment: {
      structureWallId,
      angleDeviation: 0,
      alignmentQuality: 'good',
      bottomLeft: { x: bL.x, y: floorY, z: bL.z },
      bottomRight: { x: bR.x, y: floorY, z: bR.z },
      bottomMiddle: { x: centerX, y: floorY, z: centerZ },
    },
    // Stamp only a real story: `story: 0` on an unstamped edge is an
    // invented floor, and the plan's ghost pass fades the room around it.
    ...(Number.isFinite(story) ? { story } : {}),
    isUserCreated: true,
    trim: { ...DEFAULT_WALL_TRIM },
  };
}

/**
 * Build a complete Firestore room document from polygon vertices.
 * @param {Object} params
 * @param {Array<{x: number, z: number}>} params.vertices
 * @param {Array} params.roomWalls
 * @param {string} params.structureId
 * @param {number} params.floorY
 * @param {number} params.wallHeight
 * @param {number} params.story
 * @param {string} params.userId
 * @returns {Object} Room document ready for Firestore
 */
export function buildRoomDocument({
  vertices,
  roomWalls,
  structureId,
  floorY,
  wallHeight,
  story,
  userId,
}) {
  const points = vertices.map((v) => [v.x, v.z]);
  const centroid = calculatePolygonCentroid(points);
  const area = Math.abs(calculatePolygonArea(points));

  // Build floor polygon corners (3D, at floor level)
  const floorPolygonCorners = vertices.map((v) => ({
    x: v.x,
    y: floorY,
    z: v.z,
  }));

  return {
    structureId,
    story,
    name: '',
    center: {
      x: centroid[0],
      y: floorY + wallHeight / 2,
      z: centroid[1],
    },
    dimensions: {
      // Bounding box
      x:
        Math.max(...vertices.map((v) => v.x)) -
        Math.min(...vertices.map((v) => v.x)),
      y: wallHeight,
      z:
        Math.max(...vertices.map((v) => v.z)) -
        Math.min(...vertices.map((v) => v.z)),
    },
    walls: roomWalls,
    floors: [
      {
        id: `floor_${Date.now()}`,
        center: { x: centroid[0], y: floorY, z: centroid[1] },
        dimensions: {
          x:
            Math.max(...vertices.map((v) => v.x)) -
            Math.min(...vertices.map((v) => v.x)),
          y: 0,
          z:
            Math.max(...vertices.map((v) => v.z)) -
            Math.min(...vertices.map((v) => v.z)),
        },
        polygonCorners: floorPolygonCorners,
      },
    ],
    objects: [],
    doors: [],
    windows: [],
    openings: [],
    area,
    isUserCreated: true,
    // No `|| null` fallback: the security rules resolve a room create through
    // its owner, so a null here is rejected — silently, and only at write time.
    // Fail where the cause is visible instead.
    createdBy: userId,
    createdAt: new Date().toISOString(),
  };
}

// --- Snapping ---

/** The room store narrowed to `story` (a room with no storey stamped stays). */
function roomsOnStorey(roomsData, story) {
  if (!Number.isFinite(story) || !roomsData?.values) return roomsData;
  const kept = new Map();
  for (const [id, room] of roomsData.entries()) {
    if (Number.isFinite(room?.story) && room.story !== story) continue;
    kept.set(id, room);
  }
  return kept;
}

/**
 * Find the best snap target for the cursor position.
 * Priority: polygon corners > wall endpoints > free position.
 *
 * @param {{x: number, z: number}} cursorPos - World-space cursor position
 * @param {Array} structureWalls - Structure-level walls
 * @param {Map} roomsData - Room data Map
 * @param {Object} options
 * @param {number} options.threshold - Snap distance threshold
 * @param {boolean} options.shiftHeld - If true, skip snapping
 * @param {number|null} options.story - The storey being drawn on: another
 *   storey's room corners are not snap targets (stacked storeys share X/Z, so
 *   an upstairs corner sits over the ground floor with nothing drawn there).
 *   A room with no storey stamped is on every storey; null snaps to all.
 * @returns {{type: string, position: {x: number, z: number}, target: Object|null}}
 */
export function findSnapTarget(
  cursorPos,
  structureWalls,
  roomsData,
  {
    threshold = VERTEX_SNAP_THRESHOLD,
    shiftHeld = false,
    anchors = null,
    story = null,
  } = {}
) {
  if (shiftHeld) {
    return {
      type: 'free',
      position: { x: cursorPos.x, z: cursorPos.z },
      target: null,
      guides: [],
    };
  }

  // CAD-style inference: gather nearby point + line constraints and resolve a
  // single snap by priority (room corner > wall corner > two-line intersection
  // > single-line projection). Final wall alignment is applied per-edge at
  // polygon close by resolvePolygonToWalls.
  const rooms = roomsOnStorey(roomsData, story);
  const { points } = gatherPointConstraints(cursorPos, structureWalls, rooms);
  const { lines } = gatherLineConstraints(
    cursorPos,
    structureWalls,
    rooms,
    anchors?.prev ?? null
  );
  const snap = resolveSnap(
    cursorPos,
    { points, lines },
    { pointTolerance: threshold }
  );

  if (snap.type !== 'free') {
    return {
      type: snap.type,
      position: snap.position,
      target: snap.wallId ? { wallId: snap.wallId } : null,
      guides: snap.guides,
      provisional: snap.provisional ?? false,
      wallId: snap.wallId,
      candidates: snap.candidates,
    };
  }

  // Fallback: existing 45° angle snap relative to prev / first vertex.
  const prev = anchors?.prev ?? null;
  const next = anchors?.next ?? null;
  if (prev && next) {
    const result = snapVertexToNeighborAngles({
      cursor: cursorPos,
      prev,
      next,
      shiftHeld,
    });
    if (result.snap.type !== 'none') {
      return {
        type: 'angle',
        position: result.position,
        target: {
          prev,
          next,
          prevSnapped: result.snap.prevSnapped,
          nextSnapped: result.snap.nextSnapped,
          snapType: result.snap.type,
        },
        guides: [],
      };
    }
  } else if (prev) {
    const angleResult = snapVertexToAnchorAngle({
      cursor: cursorPos,
      anchor: prev,
      shiftHeld,
    });
    if (angleResult.snapped) {
      return {
        type: 'angle',
        position: angleResult.position,
        target: {
          prev,
          next: null,
          prevSnapped: true,
          nextSnapped: false,
          snapType: 'single',
        },
        guides: [],
      };
    }
  }

  return {
    type: 'free',
    position: { x: cursorPos.x, z: cursorPos.z },
    target: null,
    guides: [],
  };
}
